<template>
    <component :is="tag" class="simple-slider">
        <div ref="containerRef" class="simple-slider-container">
            <component :is="contentTag" ref="contentRef" class="simple-slider-content" :style="style">
                <slot/>
            </component>
        </div>

        <button type="button" :aria-label="$dictionary.get('Generic.Prev')" class="prev-arrow" @click="prev">
            <slot name="left-arrow" :active="leftOverflow">
                <div v-if="leftOverflow">
                    <CIcon name="arrow-left" width="15" height="15"/>
                </div>
            </slot>
        </button>
        <button type="button" :aria-label="$dictionary.get('Generic.Next')" class="next-arrow" @click="next">
            <slot name="right-arrow" :active="rightOverflow">
                <div v-if="rightOverflow">
                    <CIcon name="arrow-right" width="15" height="15"/>
                </div>
            </slot>
        </button>
    </component>
</template>

<script setup lang="ts">
import { StyleValue, ref, computed, nextTick, onMounted, onBeforeUnmount, watch, useTemplateRef } from 'vue';
import breakpointsState from '@/core/responsive/breakpoints/breakpointsState.observable';

type OverflowLocation = 'left' | 'right' | 'both' | 'none';

const props = defineProps({
    tag: { type: String, default: 'div' },
    contentTag: { type: String, default: 'div' },
    slidesToScroll: { type: Number, default: 0 },
    speed: { type: Number, default: 500 },
    spaceBetween: { type: Boolean, default: false }
});

const overflowLoc = ref<OverflowLocation>('none');
const translateX = ref<number>(0);
const isScrolling = ref<boolean>(false);
const observer = ref<any>(null);

const containerRef = useTemplateRef<HTMLElement | null>('containerRef');
const contentRef = useTemplateRef<HTMLElement | null>('contentRef');

const style = computed((): StyleValue => {
    return {
        transition: `transform ${props.speed}ms ease-in-out`,
        transform: `translate(${translateX.value}px, 0)`,
        ...(props.spaceBetween && { justifyContent: 'space-between' })
    };
});

const leftOverflow = computed((): boolean => {
    return overflowLoc.value === 'left' || overflowLoc.value === 'both';
});

const rightOverflow = computed((): boolean => {
    return overflowLoc.value === 'right' || overflowLoc.value === 'both';
});

const activeBreakpoint = computed(() => {
    return breakpointsState.activeBreakpoint;
});

function prev() {
    if (isScrolling.value) return;

    isScrolling.value = true;

    const containerMetrics = containerRef.value!.getBoundingClientRect();
    const contentMetrics = contentRef.value!.getBoundingClientRect();

    const overflowingElements = getOverflowingElements('left');

    let transformX = 0;

    if (props.slidesToScroll > 0) {
        const lastElement = overflowingElements[overflowingElements.length - 1];
        const lastElementMetrics = lastElement.getBoundingClientRect();
        const currentOffSet = containerMetrics.left - contentMetrics.left;
        transformX = (containerMetrics.left - currentOffSet) - lastElementMetrics.left;
    } else { // Slide till prev page
        const containerWidth = containerMetrics.width;

        let lastElement: Element | null = null;
        let currentWidth = 0;

        for (const element of overflowingElements) {
            if ((currentWidth += element.scrollWidth) > containerWidth) break;
            lastElement = element;
        }

        if (lastElement) {
            const lastElementMetrics = lastElement.getBoundingClientRect();
            const currentOffSet = containerMetrics.left - contentMetrics.left;
            transformX = (containerMetrics.left - currentOffSet) - lastElementMetrics.left;
        }
    }

    translateX.value = transformX;
}

function next() {
    if (isScrolling.value) return;

    isScrolling.value = true;

    const containerMetrics = containerRef.value!.getBoundingClientRect();
    const contentMetrics = contentRef.value!.getBoundingClientRect();

    const overflowingElements = getOverflowingElements('right');

    let transformX = 0;

    if (props.slidesToScroll > 0) {
        const lastElement = overflowingElements[overflowingElements.length - 1];
        const lastElementMetrics = lastElement.getBoundingClientRect();
        const currentOffSet = containerMetrics.left - contentMetrics.left;
        transformX = (containerMetrics.right - currentOffSet) - lastElementMetrics.right;
    } else { // Slide till next page
        const containerWidth = containerMetrics.width;

        let lastElement: Element | null = null;
        let currentWidth = 0;

        for (const element of overflowingElements) {
            if ((currentWidth += element.scrollWidth) > containerWidth) break;
            lastElement = element;
        }

        if (lastElement) {
            const lastElementMetrics = lastElement.getBoundingClientRect();
            const currentOffSet = containerMetrics.left - contentMetrics.left;
            transformX = (containerMetrics.right - currentOffSet) - lastElementMetrics.right;
        }
    }

    translateX.value = transformX;
}

function getOverflowingElements(direction: OverflowLocation): Element[] {
    const overflowingElements: Element[] = [];
    const elements = direction === 'right' ? Array.from(contentRef.value!.children) : Array.from(contentRef.value!.children).reverse();

    for (const element of elements) {
        if (props.slidesToScroll > 0 && (overflowingElements.length + 1 > props.slidesToScroll)) break;

        if (containerRef.value) {
            const overflowLocation = determineOverflow(element, containerRef.value);

            if (overflowLocation === direction) {
                overflowingElements.push(element);
            }
        }
    }

    return overflowingElements;
}

function determineOverflow(content: Element, container: Element): OverflowLocation {
    if (!content || !container) return 'none';

    const containerMetrics = container.getBoundingClientRect();
    const containerMetricsRight = Math.floor(containerMetrics.right);
    const containerMetricsLeft = Math.floor(containerMetrics.left);
    const contentMetrics = content.getBoundingClientRect();
    const contentMetricsRight = Math.floor(contentMetrics.right);
    const contentMetricsLeft = Math.floor(contentMetrics.left);
    if (containerMetricsLeft > contentMetricsLeft && containerMetricsRight < contentMetricsRight) {
        return 'both';
    } else if (contentMetricsLeft < containerMetricsLeft) {
        return 'left';
    } else if (contentMetricsRight > containerMetricsRight) {
        return 'right';
    } else {
        return 'none';
    }
}

function setOverflowLoc() {
        const lOverflow = getOverflowingElements('left').length > 0;
        const rOverflow = getOverflowingElements('right').length > 0;

        overflowLoc.value = lOverflow && rOverflow ? 'both' : lOverflow ? 'left' : rOverflow ? 'right' : 'none';
    }

function transitionEnded() {
    setOverflowLoc();
    isScrolling.value = false;
}

watch(() => activeBreakpoint.value, () => {
    setOverflowLoc();
});

onMounted(() => {
    contentRef.value!.addEventListener('transitionend', transitionEnded);

    observer.value = new MutationObserver(() => {
        setOverflowLoc();
    });

    observer.value.observe(
        contentRef.value,
        { childList: true, subtree: true }
    );

    nextTick(() => {
        setOverflowLoc();
    });
});

onBeforeUnmount(() => {
    contentRef.value?.removeEventListener('transitionend', transitionEnded);

    if (observer.value) {
        observer.value.disconnect();
        observer.value = null;
    }
});
</script>

<style lang="less" scoped>
.simple-slider {
    position: relative;

    height: 100%;
    width: 100%;
}

.simple-slider-container {
    overflow: hidden;
    white-space: nowrap;
    position: relative;
    font-size: 0;
    height: 100%;
    width: 100%;

    display: flex;
}

.simple-slider-content {
    position: relative;
    height: 100%;
    width: 100%;

    display: flex;
}

.prev-arrow,
.next-arrow {
    position: absolute;
    top: 0;
    height: 100%;
    transform: translateY(-50%);
    cursor: pointer;
}

.prev-arrow {
    left: -10px;
}

.next-arrow {
    right: -10px;
}
</style>
